import { forwardRef } from 'react';
import { getCustomerBasedProfile } from 'app/main/apps/common/AuraFunctions';
import { fitText } from 'app/main/apps/e-commerce/order/tabs/boxLabel';
import { QUIET_ZONE, barRects, barcodeWidthIn, skuBars, skuProductText } from './skuLabelRules';

const GAP = '0.07in';
const LABEL_STYLE = {
  width: '6in',
  height: '3.98in',
  boxSizing: 'border-box',
  // PGSS-5869: the outline, as the box label has.
  border: '2px solid #000',
  padding: '0.09in',
  display: 'flex',
  flexDirection: 'column',
  gap: GAP,
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#000',
  background: '#fff',
  overflow: 'hidden',
  breakInside: 'avoid',
};

const CELL = { flexShrink: 0, minWidth: 0, boxSizing: 'border-box', border: '2px solid #000', padding: '0.05in 0.08in' };
// The label's width holds about this many bold characters a line. A long name shrinks to fit rather than being cut.
const NAME_FIT = { basePx: 26, charsPerLine: 34, lines: 2, minPx: 10 };
const SHAPE_FIT = { basePx: 18, charsPerLine: 48, lines: 1, minPx: 10 };
// As the box label's top row: the address shrinks to fit two lines beside the logo.
const ADDRESS_FIT = { basePx: 9, charsPerLine: 62, lines: 2, minPx: 7 };

const fitted = (text, fit) => {
  const { fontPx, lines } = fitText(text, fit);
  return { fontSize: `${fontPx}px`, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflow: 'hidden', overflowWrap: 'anywhere' };
};

/**
 * PGSS-5864: one 6" wide × 4" tall SKU label, in the box label's boxes: the logo, the product in full, the Code 128
 * barcode of the SKU, and the SKU under it. A stock piece has no order or customer, so those lines aren't here.
 * PGSS-5869: the company name and address beside the logo, as on the box label.
 */
export function SkuLabel({ product, isLast, companyName, companyAddress }) {
  const sku = String(product?.sku || '').trim();
  const bars = skuBars(sku);
  const width = bars.length + QUIET_ZONE * 2;
  const widthIn = bars ? barcodeWidthIn(width) : null;
  const { text, shape } = skuProductText(product);
  return (
    <div
      className="sku-label"
      data-testid="sku-label"
      style={{ ...LABEL_STYLE, pageBreakAfter: isLast ? 'auto' : 'always', breakAfter: isLast ? 'auto' : 'page' }}
    >
      <div style={{ ...CELL, display: 'flex', alignItems: 'center', gap: '0.08in' }}>
        <img data-testid="sku-label-logo" src={getCustomerBasedProfile.companyLogo} alt={companyName || 'logo'} style={{ height: '0.3in', objectFit: 'contain' }} />
        <div data-testid="sku-label-company" style={{ flex: 1, minWidth: 0, textAlign: 'center', lineHeight: 1.15 }}>
          <div style={{ fontSize: '12px', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{companyName || ''}</div>
          <div data-testid="sku-label-company-address" style={fitted(companyAddress || '', ADDRESS_FIT)}>{companyAddress || ''}</div>
        </div>
      </div>
      <div style={{ ...CELL, lineHeight: 1.15 }}>
        <div data-testid="sku-label-name" style={{ fontWeight: 800, ...fitted(text, NAME_FIT) }}>{text}</div>
        {shape && <div data-testid="sku-label-shape" style={{ fontWeight: 700, ...fitted(shape, SHAPE_FIT) }}>{shape}</div>}
      </div>
      <div style={{ ...CELL, flex: 1, flexShrink: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.05in' }}>
        {bars && widthIn ? (
          <svg
            data-testid="sku-label-barcode"
            viewBox={`0 0 ${width} 1`}
            preserveAspectRatio="none"
            shapeRendering="crispEdges"
            style={{ width: `${widthIn.toFixed(4)}in`, height: '1.6in', flexShrink: 1, minHeight: 0 }}
            role="img"
            aria-label={`Barcode ${sku}`}
          >
            <rect x="0" y="0" width={width} height="1" fill="#fff" />
            {barRects(bars).map((r) => (
              <rect key={r.x} x={r.x} y="0" width={r.w} height="1" fill="#000" />
            ))}
          </svg>
        ) : (
          <div style={{ fontSize: '12px' }}>No barcode</div>
        )}
        <div data-testid="sku-label-text" style={{ flexShrink: 0, fontFamily: 'Courier New, monospace', fontSize: '26px', fontWeight: 700, letterSpacing: '0.04em', overflowWrap: 'anywhere', textAlign: 'center', lineHeight: 1.1 }}>
          {sku}
        </div>
      </div>
    </div>
  );
}

/** Every label, one per printed page. */
const SkuLabels = forwardRef(function SkuLabels({ products, companyName, companyAddress }, ref) {
  return (
    <div ref={ref} className="sku-labels">
      {products.map((p, i) => (
        <SkuLabel key={p.id} product={p} isLast={i === products.length - 1} companyName={companyName} companyAddress={companyAddress} />
      ))}
    </div>
  );
});

export default SkuLabels;

